import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  DOCUMENT,
  inject
} from '@angular/core';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';
import { TimeQuestionV3 } from '../../../classes/question-time';
import { Subscription } from 'rxjs';
import { IonItem } from '@ionic/angular';
import chroma from 'chroma-js';

import { CoreUtilsService } from '@bepatient/core';

@Component({
  selector: 'bepatient-input-time-v3',
  templateUrl: './input-time.component.html',
  styleUrls: ['./input-time.component.scss'],
  standalone: false
})
export class InputTimeComponentV3
  extends QuestionBaseV3ComponentDirective
  implements AfterViewInit, OnDestroy, DoCheck
{
  document = inject(DOCUMENT);
  private utils = inject(CoreUtilsService);

  _color: string;
  contrast: string;
  @Input() question: TimeQuestionV3;

  placeholder: string;
  isBlur = true;
  hasError: boolean;
  subscription: Subscription;
  fieldErrors: Array<string>;
  dirty: boolean;
  userLocale: string;
  timeValue: string = undefined;
  pickerCurrentTime: string = undefined;
  hourCycleFormat: string = 'h23';

  @ViewChild(IonItem, { read: ElementRef, static: true }) ionItem: ElementRef;

  @Input()
  set color(c: string) {
    this._color = c;
    this.contrast =
      chroma(c || '#fafafa').luminance() < 0.6 ? '#fafafa' : '#2e2e2e';
  }
  get color() {
    return this._color;
  }

  get value() {
    return this.formGroup.get(this.question.key).value;
  }

  constructor() {
    super();
    this.userLocale = localStorage.getItem('locale') || '';
  }

  ngAfterViewInit(): void {
    this.ionItem.nativeElement.style.setProperty(
      '--input-valid-background',
      chroma(this.color).alpha(0.1).css()
    );
  }

  ngAfterContentInit(): void {
    // this.hourCycleFormat =
    //   this.question?.format && this.question.format === '12' ? 'h12' : 'h23';
    this.hourCycleFormat = /hh/.test(this.question?.format || '')
      ? 'h12'
      : 'h23';
    this.initializePickerValue();
  }

  ngDoCheck(): void {
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  onBlur(): void {
    this.isBlur = true;
    this.formGroup.get(this.question.key).markAsDirty();
  }

  onFocus(): void {
    this.isBlur = false;
    this.initializePickerValue();
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  onInput(): void {
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  onChange(value: string): void {
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.onTimeSelected(value);
  }

  onConfirm(value: string): void {
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.onTimeSelected(value);
    this.isBlur = true;
  }

  isEmpty(): boolean {
    return !this.value;
  }

  onCancel(): void {
    // User dismissed picker without changes
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.isBlur = true;
  }

  onClear(): void {
    // Explicit clear -> empty + show required after close
    this.pickerCurrentTime = void 0;
    this.formGroup.controls[this.question.key].patchValue('');
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.isBlur = true;
  }

  private initializePickerValue(): void {
    const currentValue = this.formGroup.get(this.question.key).value;

    if (currentValue) {
      // Sync picker to the existing control value without changing it
      // (this will also normalize HH:mm ↔ hh:mm a as needed)
      this.setFormattedTimeInPicker(currentValue);
    } else {
      // Set a default picker time (now) but DO NOT write to form control
      const now = new Date();
      const HH = now.getHours().toString().padStart(2, '0');
      const MM = now.getMinutes().toString().padStart(2, '0');
      this.pickerCurrentTime = `${HH}:${MM}`; // picker expects 24h
    }
  }

  private onTimeSelected(timeString: string): void {
    if (timeString) {
      // Normalize Ionic values like '2025-09-16T01:26' to '01:26'
      const raw = String(timeString);
      const isoTimeMatch = raw.match(/T(\d{2}):(\d{2})/);
      if (isoTimeMatch) {
        timeString = `${isoTimeMatch[1]}:${isoTimeMatch[2]}`;
      }

      const expects12h = /hh/.test(this.question?.format || '');
      let formattedTime: string;

      const is24 = this.utils.isValidDateTimeOrTimeString(
        timeString,
        '24h-time'
      );
      const is12 = this.utils.isValidDateTimeOrTimeString(
        timeString,
        '12h-time'
      );

      if (is24) {
        if (expects12h) {
          // Picker returns HH:mm even in h12 mode; convert for display/submission
          const { userFormat, isoFormat } = this.utils.getValidDateFormat(
            'hh:mm',
            timeString
          );
          formattedTime = userFormat; // e.g., 01:26 AM
          this.pickerCurrentTime = isoFormat; // e.g., 01:26 -> 01:26 (HH:mm)
        } else {
          formattedTime = timeString; // already HH:mm and expected 24h
          this.pickerCurrentTime = timeString; // picker uses 24h
        }
      } else if (is12) {
        // Normalize 12h -> 24h for picker, keep display based on expectation
        const { userFormat, isoFormat } = this.utils.getValidDateFormat(
          'hh:mm',
          timeString
        );
        formattedTime = expects12h ? userFormat : isoFormat; // if expecting 24h, show HH:mm
        this.pickerCurrentTime = isoFormat || void 0; // HH:mm for picker
      } else {
        // Last attempt based on declared format
        const base = expects12h ? 'hh:mm' : 'HH:mm';
        const { userFormat, isoFormat } = this.utils.getValidDateFormat(
          base,
          timeString
        );
        formattedTime = userFormat;
        this.pickerCurrentTime = isoFormat || void 0;
      }

      this.formGroup.controls[this.question.key].patchValue(formattedTime);
    } else {
      // When date picker is cleared, reset the value to undefined
      this.pickerCurrentTime = void 0;
      this.formGroup.controls[this.question.key].setValue(void 0);
    }
  }

  private setFormattedTimeInPicker(value): void {
    const expects12h = /hh/.test(this.question?.format || '');

    if (this.utils.isValidDateTimeOrTimeString(value, '24h-time')) {
      // Value is HH:mm
      if (expects12h) {
        // Convert HH:mm -> hh:mm a for display; picker stays 24h
        const { userFormat, isoFormat } = this.utils.getValidDateFormat(
          'hh:mm',
          value
        );
        this.pickerCurrentTime = isoFormat; // HH:mm for picker
        this.formGroup.controls[this.question.key].patchValue(userFormat); // e.g., 01:22 PM
      } else {
        // Expected 24h; keep as-is
        this.pickerCurrentTime = value;
        this.formGroup.controls[this.question.key].patchValue(value);
      }
      return;
    }

    if (this.utils.isValidDateTimeOrTimeString(value, '12h-time')) {
      // Value is hh:mm AM/PM
      if (expects12h) {
        // Display 12h; picker needs 24h
        const { isoFormat } = this.utils.getValidDateFormat('hh:mm', value);
        this.pickerCurrentTime = isoFormat; // HH:mm for picker
        this.formGroup.controls[this.question.key].patchValue(value); // keep 12h for display/submission
      } else {
        // Expected 24h; convert to HH:mm
        const { userFormat } = this.utils.getValidDateFormat('HH:mm', value);
        this.pickerCurrentTime = userFormat; // HH:mm
        this.formGroup.controls[this.question.key].patchValue(userFormat);
      }
      return;
    }

    // Unknown or empty -> clear
    this.pickerCurrentTime = void 0;
    this.formGroup.controls[this.question.key].setValue(void 0);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
